// The Market: SEND resources to a city · YOUR OFFERS (create, cancel) · OTHER OFFERS (search, accept) · MOVEMENT (merchants
// out and back). Merchants carry 1,000 each; travel at the world's merchant speed.
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { MarketSearchOffer } from "@/api/types";
import { art } from "@/kit/art";
import { Collapsible, Countdown, GoldRange, ResMedal, ScreenHero, Stepper, StickyBar, TimeBar, XYField } from "@/kit/city";
import { IconArrowRight, IconBookmark, IconChevronDown, IconClose, IconExchange } from "@/kit/icons";
import { Confirm, Sheet } from "@/kit/overlays";
import { Button, Card, CardHead, Disc, Empty, Medallion, Row, RowText, Seg, cx } from "@/kit/primitives";
import { useNow } from "@/state/clock";
import { useRememberedOpen } from "@/state/collapse";
import { useMarketTarget } from "@/state/marketTarget";
import { RESOURCE_KEYS, RESOURCE_NAMES, type ResourceKey } from "@/game/constants";
import { abbrev, fmtCoords, fmtNum, parseCoords, plural } from "@/game/format";
import { fmtClock, fmtDuration } from "@/game/time";
import { WORLD, fieldsBetween } from "@/game/rules";
import { cityCoords, merchantMovements, merchantTravelMs, merchantsNeeded, type MerchantMove } from "@/game/city";
import { CityPage, useCity } from "./CityRoute";
import { SavedTargets, bookmarkList, useBookmarks } from "./Targets";

type Tab = "send" | "offers" | "others" | "movement";
const resIcon = (r: ResourceKey) => art.hud[r === "r_w" ? "wood" : r === "r_c" ? "clay" : "iron"];

export function Market() {
  const { tab: tabP } = useParams();
  const { v, base } = useCity();
  const nav = useNavigate();
  const tab = (["send", "offers", "others", "movement"].includes(tabP ?? "") ? tabP : "send") as Tab;
  const setTab = (t: Tab) => nav(`${base}/market/${t}`, { replace: true });
  if (!v) return <CityPage hud={false}>{null}</CityPage>;
  const mi = v.market_info;
  const moves = merchantMovements(v);
  const inTransit = moves.filter((m) => m.dir === "out").length;
  return (
    <CityPage hud={false}>
      <div className="stack">
        <ScreenHero
          title="Market"
          art={art.hero.market}
          artWidth="56%"
          onBack={() => nav(base)}
          onGear={() => nav(`${base}/hall`)}
          gearLabel="City overview"
          stats={[
            { label: "Merchants", value: `${mi?.available_merchants ?? 0} / ${mi?.total_merchants ?? 0}`, tone: mi && mi.available_merchants === 0 ? ("crim" as const) : undefined },
            { label: "Carry each", value: fmtNum(WORLD.market.carry) },
            { label: "Open offers", value: mi?.your_offers?.length ?? 0 },
            { label: "In transit", value: inTransit },
          ]}
        />
        <Seg value={tab} onChange={setTab} options={[{ value: "send", label: "Send" }, { value: "offers", label: "Your offers" }, { value: "others", label: "Other offers" }, { value: "movement", label: "Movement", badge: moves.filter((m) => m.dir === "in").length || undefined }]} />
        {tab === "send" && <SendTab />}
        {tab === "offers" && <YourOffersTab />}
        {tab === "others" && <OtherOffersTab />}
        {tab === "movement" && <MovementTab />}
      </div>
    </CityPage>
  );
}

// ------------------------------------------------------------------ SEND

function SendTab() {
  const { v, wid, vid, actions, base } = useCity();
  const nav = useNavigate();
  const now = useNow();
  // ?x=&y= — the map's city sheet RESOURCES button fills the target in (Ben, Oct 13 2026)
  const [params] = useSearchParams();
  // otherwise the last target entered in this world — kept after sending and across city switches, like the Rally
  // Point's (state/marketTarget; Ben, Oct 14 2026)
  const px = params.get("x"), py = params.get("y");
  const fromUrl = !!(px && py && /^\d+$/.test(px) && /^\d+$/.test(py));
  const remembered = useMarketTarget((s) => s.byWorld[String(wid)]);
  const rememberTarget = useMarketTarget((s) => s.set);
  const [x, setXState] = useState(fromUrl ? px! : remembered?.x ?? "");
  const [y, setYState] = useState(fromUrl ? py! : remembered?.y ?? "");
  const setXY = (nx: string, ny: string) => { setXState(nx); setYState(ny); rememberTarget(wid, { x: nx, y: ny }); };
  // a target arriving from the map becomes the remembered one too
  useEffect(() => {
    if (fromUrl) rememberTarget(wid, { x: px!, y: py! });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [amt, setAmt] = useState<Record<ResourceKey, number>>({ r_w: 0, r_c: 0, r_i: 0 });
  const [saved, setSaved] = useState(false);
  const [debounced, setDebounced] = useState<string | null>(null);
  const target = x && y ? `${Number(x)},${Number(y)}` : null;
  useEffect(() => { const t = window.setTimeout(() => setDebounced(target), 500); return () => window.clearTimeout(t); }, [target]);
  const look = useQuery({ queryKey: ["target-name", wid, vid, debounced], queryFn: () => api.rally.prepare({ wid, vid }, debounced!, ["t_sf"]), enabled: !!debounced, staleTime: 60_000, retry: false });
  const bm = useBookmarks();
  if (!v) return null;
  const mi = v.market_info;
  const from = cityCoords(v);
  const to = parseCoords(target);
  const own = from && to && from[0] === to[0] && from[1] === to[1];
  const free = mi?.available_merchants ?? 0;
  const need = merchantsNeeded(amt);
  const total = amt.r_w + amt.r_c + amt.r_i;
  const travel = from && to ? merchantTravelMs(from, to) : NaN;
  const tv = debounced ? look.data?.city_details?.[debounced]?.target_village : undefined;
  const name = tv?.city_name?.replace(/<[^>]+>/g, "").replace(/\(Barbarian\)/i, "").trim();
  const recent = bookmarkList(bm.data?.recent_bookmarks).slice(0, 3);
  const capLeft = (r: ResourceKey) => Math.max(0, Math.min(v.resources?.[r] ?? 0, (free - merchantsNeeded({ ...amt, [r]: 0 })) * WORLD.market.carry));
  const sendable = total > 0 && !!to && !own && need <= free;
  return (
    <>
      <Card>
        <div className="target-row">
          <h2>Target</h2>
          <XYField x={x} y={y} onChange={(nx, ny) => setXY(nx, ny)} />
          <Disc size="sm" label="Saved targets" onClick={() => setSaved(true)}><IconBookmark /></Disc>
          <button type="button" className="link caps" onClick={() => setSaved(true)}>Saved <IconChevronDown style={{ width: 12, height: 12 }} /></button>
        </div>
        <div className="target-line">
          {!to ? <span className="muted">Enter the target's X and Y, or pick a saved target.</span>
            : own ? <span className="crimtxt">That is this city.</span>
            : <>{name ? <b className="gold">{name} </b> : null}{fmtCoords(to)} · {from ? fieldsBetween(from, to).toFixed(2) : "—"} fields{look.isError ? <span className="crimtxt"> · no city there</span> : null}</>}
        </div>
        {recent.length > 0 && (
          <div className="recent"><span className="eyebrow">Recent</span>{recent.map((b, i) => <button key={i} type="button" className="chipbtn" onClick={() => { const p = parseCoords(b.coords); if (p) setXY(String(p[0]), String(p[1])); }}>{b.name} {fmtCoords(b.coords)}</button>)}</div>
        )}
      </Card>

      <Card>
        <CardHead title="Resources" aside={`${fmtNum(total)} to send`} />
        <div className="rows">
          {RESOURCE_KEYS.map((r) => {
            const n = amt[r];
            const max = capLeft(r);
            return (
              <Row key={r} className={cx("troop-row", n > 0 && "goldedge")}>
                <ResMedal r={r} size={60} />
                <RowText line={<b className="creamt" style={{ fontWeight: 600, fontFamily: "var(--title)", letterSpacing: ".08em", textTransform: "uppercase" }}>{RESOURCE_NAMES[r]}</b>} meta={`${abbrev(v.resources?.[r])} in city`} />
                <GoldRange value={n} max={Math.max(max, n)} step={100} onChange={(val) => setAmt((a) => ({ ...a, [r]: val }))} label={`${RESOURCE_NAMES[r]} to send`} />
                <input type="text" inputMode="numeric" className="c-num" style={{ width: "calc(var(--u) * 120)" }} value={n ? String(n) : ""} placeholder="0" aria-label={`${RESOURCE_NAMES[r]} amount`}
                  onChange={(e) => setAmt((a) => ({ ...a, [r]: Math.max(0, Math.min(v.resources?.[r] ?? 0, Number(e.target.value.replace(/[^\d]/g, "") || 0))) }))} />
                <button type="button" className="link caps" onClick={() => setAmt((a) => ({ ...a, [r]: capLeft(r) }))}>Max</button>
              </Row>
            );
          })}
        </div>
        <div className="merch-strip">
          <div><div className="eyebrow">Merchants</div><div className="muted" style={{ marginTop: 6, font: "400 var(--fs-meta)/1.2 var(--text)" }}>{need} of {mi?.total_merchants ?? 0} needed · {fmtNum(WORLD.market.carry)} each{need > free ? <span className="crimtxt"> · only {free} free</span> : null}</div></div>
          <div className="carts">
            {Array.from({ length: Math.min(12, mi?.total_merchants ?? 0) }, (_, i) => {
              const busy = i >= free;
              const loaded = !busy && i < need;
              return <div key={i} className={cx("cart", loaded && "loaded", busy && "busy")}><Medallion src={art.market.cart} alt="" size={60} glow={loaded ? "ember" : undefined} dim={busy} /><span>{busy ? "Away" : loaded ? "Loaded" : "Idle"}</span></div>;
            })}
            {(mi?.total_merchants ?? 0) > 12 && <span className="muted">+{(mi?.total_merchants ?? 0) - 12}</span>}
          </div>
        </div>
      </Card>

      <StickyBar>
        <div className="line">
          <span>{Number.isFinite(travel) && to && !own ? <>Travel <b>{fmtDuration(travel)}</b> · arrives <b>{fmtClock(now + travel, true)}</b></> : <span className="muted">Pick a target and amounts</span>}</span>
          <span className="r">{Number.isFinite(travel) && to && !own ? <>returns empty in <b>{fmtDuration(travel * 2)}</b></> : ""}</span>
        </div>
        <div className="acts">
          <Button kind="crimson" disabled={!sendable} busy={actions.isBusy("send_res")}
            onClick={async () => {
              if (!to) return;
              const r = await actions.run("send_res", () => api.market.sendResources({ wid, vid }, { target_x: to[0], target_y: to[1], ...amt }), { success: `${fmtNum(total)} resources on their way to ${name ?? fmtCoords(to)}.` });
              if (r) { setAmt({ r_w: 0, r_c: 0, r_i: 0 }); nav(`${base}/market/movement`); }
            }}>
            Send resources
          </Button>
        </div>
      </StickyBar>
      <SavedTargets open={saved} onClose={() => setSaved(false)} onPick={(c) => { const p = parseCoords(c); if (p) setXY(String(p[0]), String(p[1])); setSaved(false); }} />
    </>
  );
}

// ------------------------------------------------------------------ YOUR OFFERS

function YourOffersTab() {
  const { v, wid, vid, actions } = useCity();
  const [give, setGive] = useState<ResourceKey>("r_w");
  const [want, setWant] = useState<ResourceKey>("r_c");
  const [giveN, setGiveN] = useState(100);
  const [wantN, setWantN] = useState(100);
  const [hours, setHours] = useState(1);
  const [copies, setCopies] = useState(1);
  const [cancel, setCancel] = useState<number | null>(null);
  if (!v) return null;
  const mi = v.market_info;
  const free = mi?.available_merchants ?? 0;
  const perCopy = Math.max(1, Math.ceil(giveN / WORLD.market.carry));
  const maxCopies = Math.max(1, Math.floor(free / perCopy));
  const n = Math.min(copies, maxCopies);
  const rate = giveN ? wantN / giveN : 0;
  const enough = (v.resources?.[give] ?? 0) >= giveN * n && free >= perCopy * n;
  const offers = mi?.your_offers ?? [];
  const held = offers.reduce((a, o) => a + o.num_offers * Math.ceil(o.offer_amount / WORLD.market.carry), 0);
  const step = WORLD.market.resourcesMin;
  return (
    <>
      <Card>
        <CardHead title="Create offer" aside={`uses ${perCopy} merchant${perCopy === 1 ? "" : "s"} per copy`} />
        <div className="offer-grid">
          <span className="eyebrow">You give</span>
          <div className="picks">{RESOURCE_KEYS.map((r) => <ResMedal key={r} r={r} size={64} selected={give === r} onClick={() => setGive(r)} />)}</div>
          <Stepper value={giveN} min={step} max={Math.max(step, v.resources?.[give] ?? 0)} step={step} onChange={setGiveN} label="Amount you give" />
          <span className="eyebrow">You want</span>
          <div className="picks">{RESOURCE_KEYS.map((r) => <ResMedal key={r} r={r} size={64} selected={want === r} onClick={() => setWant(r)} />)}</div>
          <Stepper value={wantN} min={step} max={999999} step={step} onChange={setWantN} label="Amount you want" />
        </div>
        <div className="offer-opts">
          <div><div className="eyebrow">Max travel</div><div className="muted hint">Only buyers within {hours} hour{hours === 1 ? "" : "s"} can accept</div></div>
          <Stepper value={hours} min={1} max={mi?.max_trade_radius || WORLD.market.maxTradeRadiusHours} onChange={setHours} format={(h) => `${h} h`} label="Max travel hours" />
          <div><div className="eyebrow">Copies</div><div className="muted hint">Each copy holds {perCopy} merchant{perCopy === 1 ? "" : "s"}</div></div>
          <Stepper value={n} min={1} max={maxCopies} onChange={setCopies} label="Copies" />
        </div>
        <div className="c-note left">Rate <b className={rate >= 1 ? "greentxt" : "crimtxt"}>{giveN && wantN ? `${(giveN / Math.min(giveN, wantN)).toFixed(giveN % Math.min(giveN, wantN) ? 2 : 0)} : ${(wantN / Math.min(giveN, wantN)).toFixed(wantN % Math.min(giveN, wantN) ? 2 : 0)}` : "—"}</b> · you hold {fmtNum(giveN * n)} {RESOURCE_NAMES[give].toLowerCase()} until a buyer accepts{give === want ? <span className="crimtxt"> · give and want are the same resource</span> : null}</div>
        <Button kind="crimson" wide disabled={!enough || give === want} busy={actions.isBusy("offer")}
          onClick={async () => {
            const r = await actions.run("offer", () => api.market.createOffer({ wid, vid }, { offer_type: give, offer_amount: giveN, for_type: want, for_amount: wantN, num_offers: n, trade_radius: hours }), { success: `Offer posted${n > 1 ? ` ×${n}` : ""}.` });
            if (r) setCopies(1);
          }}>
          Create offer
        </Button>
        {!enough && <div className="c-note">{free < perCopy * n ? "Not enough free merchants." : `Not enough ${RESOURCE_NAMES[give].toLowerCase()} in this city.`}</div>}
      </Card>
      <Card>
        <CardHead title="Open offers" aside={offers.length ? `${offers.length} open · ${held} merchant${held === 1 ? "" : "s"} held` : "none open"} />
        {!offers.length ? <Empty>No open offers.</Empty> : (
          <>
            <div className="offer-head"><span>You give</span><span>You want</span><span>Rate</span><span>Travel</span><span /></div>
            <div className="rows">
              {offers.map((o) => {
                const r = o.offer_amount ? o.for_amount / o.offer_amount : 0;
                return (
                  <Row key={o.id} className="offer-row">
                    <span className="muted" style={{ font: "400 var(--fs-meta)/1 var(--text)" }}>×{o.num_offers}</span>
                    <span className="cell"><Medallion src={resIcon(o.offer_type)} alt={RESOURCE_NAMES[o.offer_type]} size={44} /> {fmtNum(o.offer_amount)}</span>
                    <IconArrowRight style={{ width: 16, height: 16, color: "var(--g-text)" }} />
                    <span className="cell"><Medallion src={resIcon(o.for_type)} alt={RESOURCE_NAMES[o.for_type]} size={44} /> {fmtNum(o.for_amount)}</span>
                    <span className={cx("rate", r >= 1 ? "greentxt" : "crimtxt")}>{fmtRate(o.offer_amount, o.for_amount)}</span>
                    <span className="muted" style={{ font: "500 var(--fs-body2)/1 var(--text)", whiteSpace: "nowrap" }}>{o.trade_radius} h</span>
                    <Disc size="sm" label="Cancel offer" onClick={() => setCancel(o.id)}><IconClose /></Disc>
                  </Row>
                );
              })}
            </div>
            <div className="c-note left">Cancelling returns the resources and frees the merchant.</div>
          </>
        )}
      </Card>
      <Confirm open={cancel !== null} onClose={() => setCancel(null)} title="Cancel this offer?" text="The resources return to the warehouse and the merchants are freed." action="Cancel offer" busy={actions.isBusy("cancel_offer")}
        onConfirm={async () => { if (cancel === null) return; const r = await actions.run("cancel_offer", () => api.market.cancelOffer({ wid, vid }, cancel), { success: "Offer withdrawn." }); if (r) setCancel(null); }} />
    </>
  );
}

const fmtRate = (give: number, want: number) => {
  if (!give || !want) return "—";
  const g = Math.min(give, want);
  const a = give / g;
  const b = want / g;
  return Number.isInteger(a) && Number.isInteger(b) && a < 100 && b < 100 ? `${a} : ${b}` : (want / give).toFixed(2);
};

// ------------------------------------------------------------------ OTHER OFFERS

function OtherOffersTab() {
  const { v, wid, vid, actions, base } = useCity();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [theyGive, setTheyGive] = useState<ResourceKey | "">("");
  const [theyWant, setTheyWant] = useState<ResourceKey | "">("");
  const [hours, setHours] = useState(10);
  const [params, setParams] = useState<{ offer_type: ResourceKey | ""; search_type: ResourceKey | ""; radius: number } | null>(null);
  const [accept, setAccept] = useState<MarketSearchOffer | null>(null);
  const [copies, setCopies] = useState(1);
  const q = useQuery({ queryKey: ["offers", wid, vid, params], queryFn: () => api.market.searchOffers({ wid, vid }, params!), enabled: !!params, staleTime: 15_000 });
  if (!v) return null;
  const free = v.market_info?.available_merchants ?? 0;
  const results = q.data?.offers ?? [];
  const npc = v.store?.items?.npc_trade;
  return (
    <>
      <Card>
        <CardHead title="Search offers" aside={`${v.village?.city_name} · ${free} merchant${free === 1 ? "" : "s"} free`} />
        <div className="offer-grid search">
          <span className="eyebrow">They offer</span>
          <div className="picks"><ResMedal r="any" size={64} selected={theyGive === ""} onClick={() => setTheyGive("")} label="Any" />{RESOURCE_KEYS.map((r) => <ResMedal key={r} r={r} size={64} selected={theyGive === r} onClick={() => setTheyGive(r)} />)}</div>
          <span className="eyebrow">They want</span>
          <div className="picks"><ResMedal r="any" size={64} selected={theyWant === ""} onClick={() => setTheyWant("")} label="Any" />{RESOURCE_KEYS.map((r) => <ResMedal key={r} r={r} size={64} selected={theyWant === r} onClick={() => setTheyWant(r)} />)}</div>
        </div>
        <div className="offer-opts">
          <div><div className="eyebrow">Max travel</div></div>
          <Stepper value={hours} min={1} max={v.market_info?.max_trade_radius || WORLD.market.maxTradeRadiusHours} onChange={setHours} format={(h) => `${h} h`} label="Max travel hours" />
          <span />
          <Button kind="steel" size="md" busy={q.isFetching} onClick={() => { setParams({ offer_type: theyGive, search_type: theyWant, radius: hours }); void qc.invalidateQueries({ queryKey: ["offers", wid, vid] }); }}>Search</Button>
        </div>
      </Card>
      <Card>
        <CardHead title="Results" aside={params ? `${results.length + (npc ? 1 : 0)} offers · sorted by travel` : "search above"} />
        {!params ? <Empty>Choose what they offer and what they want, then search.</Empty> : q.isLoading ? <Empty><span className="spin" /> Searching…</Empty> : (
          <>
            <div className="offer-head"><span>They give</span><span>You pay</span><span>Rate</span><span>Travel</span><span /></div>
            <div className="rows">
              {npc && (
                <Row className="offer-row goldedge">
                  <span className="cell"><Medallion src={art.store.npc_trade} alt="" size={44} /> Any</span>
                  <IconArrowRight style={{ width: 16, height: 16, color: "var(--g-text)" }} />
                  <span className="cell"><Medallion src={art.store.npc_trade} alt="" size={44} /> Any</span>
                  <span className="rate gold" style={{ fontSize: "var(--fs-label)", letterSpacing: ".1em", textTransform: "uppercase" }}>Exchange<br /><small className="muted" style={{ textTransform: "none", letterSpacing: 0, fontStyle: "italic" }}>market rate</small></span>
                  <span className="gold" style={{ font: "500 var(--fs-body2)/1 var(--text)" }}>Instant</span>
                  <Button size="sm" onClick={() => nav(`${base}/store/resources?trade=1`)}>Trade</Button>
                </Row>
              )}
              {results.map((o) => {
                const r = o.offer_amount ? o.offer_amount / o.for_amount : 0;
                return (
                  <Row key={`${o.hostVillageId}-${o.hostIndex}`} className="offer-row">
                    <span className="cell"><Medallion src={resIcon(o.offer_type)} alt={RESOURCE_NAMES[o.offer_type]} size={44} /> {fmtNum(o.offer_amount)}</span>
                    <IconArrowRight style={{ width: 16, height: 16, color: "var(--g-text)" }} />
                    <span className="cell"><Medallion src={resIcon(o.for_type)} alt={RESOURCE_NAMES[o.for_type]} size={44} /> {fmtNum(o.for_amount)}</span>
                    <span className={cx("rate", r >= 1 ? "greentxt" : "crimtxt")}>{r.toFixed(2)}<br /><small className="muted">×{o.num_offers} cop{o.num_offers === 1 ? "y" : "ies"}</small></span>
                    <span style={{ font: "500 var(--fs-body2)/1 var(--text)", whiteSpace: "nowrap" }}>{o.duration_string}</span>
                    <Button size="sm" onClick={() => { setAccept(o); setCopies(1); }}>Accept</Button>
                  </Row>
                );
              })}
              {!results.length && <Empty>No offers match within {hours} h.</Empty>}
            </div>
            <div className="c-note left">Rate = what you get per 1 you pay. Green is even or better.</div>
          </>
        )}
      </Card>
      <Sheet open={!!accept} onClose={() => setAccept(null)} label="Accept offer" title={<h3 className="eyebrow" style={{ margin: "0 0 14px" }}>Accept offer</h3>}>
        {accept && (() => {
          const perCopy = Math.ceil(accept.for_amount / WORLD.market.carry);
          const maxCopies = Math.max(1, Math.min(accept.num_offers, Math.floor(free / perCopy), Math.floor((v.resources?.[accept.for_type] ?? 0) / accept.for_amount)));
          const n = Math.min(copies, maxCopies);
          const ok = free >= perCopy * n && (v.resources?.[accept.for_type] ?? 0) >= accept.for_amount * n;
          return (
            <div className="stack">
              <p style={{ margin: 0, font: "400 var(--fs-body)/1.4 var(--text)" }}>You pay <b className="gold">{fmtNum(accept.for_amount * n)} {RESOURCE_NAMES[accept.for_type].toLowerCase()}</b> and receive <b className="gold">{fmtNum(accept.offer_amount * n)} {RESOURCE_NAMES[accept.offer_type].toLowerCase()}</b> from {fmtCoords(accept.coords)} in {accept.duration_string}. Uses {perCopy * n} merchant{perCopy * n === 1 ? "" : "s"}.</p>
              {accept.num_offers > 1 && <div className="offer-opts"><div><div className="eyebrow">Copies</div><div className="muted hint">{accept.num_offers} available</div></div><Stepper value={n} min={1} max={maxCopies} onChange={setCopies} label="Copies" /></div>}
              <Button kind="crimson" wide disabled={!ok} busy={actions.isBusy("accept")}
                onClick={async () => { const r = await actions.run("accept", () => api.market.acceptOffer({ wid, vid }, accept.hostIndex, n, accept.hostVillageId), { success: "Trade accepted — your merchants are on their way." }); if (r) { setAccept(null); void qc.invalidateQueries({ queryKey: ["offers", wid, vid] }); } }}>
                Accept {n > 1 ? `×${n}` : ""}
              </Button>
              {!ok && <div className="c-note">{free < perCopy * n ? "Not enough free merchants." : `Not enough ${RESOURCE_NAMES[accept.for_type].toLowerCase()}.`}</div>}
            </div>
          );
        })()}
      </Sheet>
    </>
  );
}

// ------------------------------------------------------------------ MOVEMENT

function MovementTab() {
  const { v } = useCity();
  const [openOut, toggleOut] = useRememberedOpen("market.outgoing");
  const [openRet, toggleRet] = useRememberedOpen("market.returning");
  const moves = useMemo(() => (v ? merchantMovements(v) : []), [v]);
  if (!v) return null;
  const mi = v.market_info;
  const total = mi?.total_merchants ?? 0;
  const onOffer = (mi?.your_offers ?? []).reduce((a, o) => a + o.num_offers * Math.ceil(o.offer_amount / WORLD.market.carry), 0);
  const trading = moves.filter((m) => m.dir === "out").reduce((a, m) => a + Number(m.m.out_merchants ?? 0), 0);
  const incoming = moves.filter((m) => m.dir === "in" && m.kind !== "return");
  const outgoing = moves.filter((m) => m.dir === "out" && m.kind !== "return");
  const returning = moves.filter((m) => m.kind === "return");
  // render functions, not inner components (an inner component remounts every row on each clock tick)
  const chips = ({ m }: { m: MerchantMove }) => {
    const inRes = m.m.in_resources ?? {};
    const outRes = m.m.out_resources ?? {};
    return (
      <div className="chips">
        {RESOURCE_KEYS.filter((r) => inRes[r]).map((r) => <span key={`i${r}`} className="chip"><Medallion src={resIcon(r)} alt="" size={38} /> <span className="greentxt">+{fmtNum(inRes[r])}</span></span>)}
        {RESOURCE_KEYS.filter((r) => outRes[r]).map((r) => <span key={`o${r}`} className="chip"><Medallion src={resIcon(r)} alt="" size={38} /> <span className="crimtxt">−{fmtNum(outRes[r])}</span> <small className="muted">{m.kind === "trade" ? "paid" : "sent"}</small></span>)}
      </div>
    );
  };
  const line = (m: MerchantMove) => {
    const inRes = m.m.in_resources ?? {};
    const outRes = m.m.out_resources ?? {};
    const sum = (o: Partial<Record<ResourceKey, number>>) => RESOURCE_KEYS.filter((r) => o[r]).map((r) => `${fmtNum(o[r])} ${RESOURCE_NAMES[r].toLowerCase()}`).join(", ");
    if (m.kind === "return") return <>Merchants returning from <b>{m.other.name}</b></>;
    if (m.dir === "in") return m.kind === "trade" ? <>Receiving <b className="gold">{sum(inRes) || "goods"}</b>{sum(outRes) ? <> for {sum(outRes)}</> : null}</> : <>Receiving <b className="gold">{sum(inRes) || sum(outRes)}</b> from <b>{m.other.name}</b></>;
    return m.kind === "trade" ? <>Trading <b className="gold">{sum(outRes)}</b> with <b>{m.other.name}</b></> : <>Sending <b className="gold">{sum(outRes)}</b> to <b>{m.other.name}</b></>;
  };
  const moveRow = ({ m, green }: { m: MerchantMove; green?: boolean }) => (
    <Row key={String(m.m.id)} className={cx(green ? "greenedge" : "goldedge")} style={{ flexWrap: "wrap" }}>
      <span className={cx("mv-disc", green && "green")}><IconExchange /></span>
      <RowText line={line(m)} meta={<>{m.kind === "trade" ? "Accepted offer" : m.kind === "return" ? "Coming home" : "Transport"} · arrives {fmtClock(m.endMs, true)}</>} />
      <div className="right"><div className="val" style={{ font: "600 var(--fs-value)/1 var(--text)" }}><Countdown endMs={m.endMs} done="Arrived" /></div></div>
      <div className="row-body"><TimeBar startMs={m.startMs} endMs={m.endMs} color={green ? "green" : "gold"} /></div>
      {m.kind !== "return" && <div className="row-body">{chips({ m })}</div>}
    </Row>
  );
  return (
    <>
      <Card>
        <CardHead title="Merchants" aside={`${fmtNum(Math.max(0, total - trading - onOffer))} of ${total} at home`} />
        <div className="c-grid3">
          {[["Trading", trading, "on a trade"], ["On offer", onOffer, "holding goods"], ["Idle", Math.max(0, total - trading - onOffer), "at home"]].map(([l, n, meta]) => (
            <div key={String(l)} className={cx("garrison-tile", !Number(n) && "dim")}><Medallion src={art.market.cart} alt="" size={64} dim={!Number(n)} glow={l === "Trading" && Number(n) ? "ember" : undefined} /><div><div className="lbl">{l}</div><div className="val">{fmtNum(Number(n))}</div><div className="muted" style={{ font: "400 var(--fs-meta)/1 var(--text)", marginTop: 4 }}>{meta}</div></div></div>
          ))}
        </div>
      </Card>
      <Card>
        <CardHead title="Incoming" badge={incoming.length || undefined} aside={incoming.length ? plural(incoming.length, "transport") : "none"} />
        {!incoming.length ? <Empty>Nothing on its way here.</Empty> : <div className="rows">{incoming.map((m) => moveRow({ m, green: true }))}</div>}
      </Card>
      <Collapsible title="Outgoing" aside={outgoing.length ? plural(outgoing.length, "transport") : "None"} open={openOut} onToggle={toggleOut}>
        <div className="rows">{outgoing.map((m) => moveRow({ m }))}</div>
      </Collapsible>
      <Collapsible title="Returning" aside={returning.length ? plural(returning.length, "transport") : "None"} open={openRet} onToggle={toggleRet}>
        <div className="rows">{returning.map((m) => moveRow({ m }))}</div>
      </Collapsible>
      <div className="c-note">Merchants come home empty once their cargo lands.</div>
    </>
  );
}

